import React from 'react';
import FacilityForm from "pages/Facility/Form";
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import {FacilityData} from "types/Facility";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {stripProperties} from "../../../services/object";

function MutateFacility() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: facility, isLoading} = useQuery(
    ['facilities', {id}],
    () => ngage.facilities.get(Number(id)),
    {enabled: !!id && auth.can('facilities:read')}
  )

  const store = useMutation((data: FacilityData) => ngage.facilities.store(data))
  const update = useMutation((data: FacilityData) => ngage.facilities.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: FacilityData, {setSubmitting}: FormikHelpers<FacilityData>) {
    const newValues = stripProperties(values, ['building_id', 'is_indoor']);

    id ? await handleUpdate(newValues) : await handleStore(newValues)
    await invalidate('facilities.all')

    setSubmitting(false)
    navigate('/facilities')
  }

  // Handle store mutation
  async function handleStore(values: FacilityData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Facility')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Facility')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: FacilityData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Facility')} Updated`, `${values.name} has been updated`)
      await invalidate(['facilities', {id}])
    } catch (error) {
      notify("error", `${termFor('Facility')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutating
  if (auth.cannot('facilities:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Facilities')]: '/facilities',
    [`New ${termFor('Facility')}`]: '/facilities/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Facilities')]: '/facilities',
    [`Edit ${termFor('Facility')}`]: `/facilities/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Facility')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <FacilityForm facility={facility} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateFacility;